import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getInventory, getInventorySyncStatus } from '../api';

export const inventoryKeys = {
  all: ['inventory'],
  lists: () => ['inventory', 'list'],
  list: (filters) => ['inventory', 'list', filters],
  allEvents: () => ['inventory', 'allEvents'],
  syncStatus: () => ['inventory', 'syncStatus'],
};

export function useInventoryList(filters, options = {}) {
  return useQuery({
    queryKey: inventoryKeys.list(filters),
    queryFn: async () => {
      const res = await getInventory(filters);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    // The server cache is delta-synced every 15 s; coming back to the tab is
    // a good moment to catch up (useInventorySyncStatus does it while open)
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

export function useAllEvents(options = {}) {
  return useQuery({
    queryKey: inventoryKeys.allEvents(),
    queryFn: async () => {
      const res = await getInventory();
      return res.data.events || [];
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// The server's inventory cache changes every few seconds during business
// hours. Rather than re-downloading the (large) list on a timer, poll the
// tiny sync-status endpoint every 15 s and refetch the lists only when the
// cache version moved — and never more often than LIST_REFETCH_MIN_MS, so a
// busy book cannot turn every poll into a full page reload. Module-level
// state so a remount (navigating back to the Dashboard) still notices a
// version change that happened while the page was away.
const SYNC_POLL_MS = 15 * 1000;
const LIST_REFETCH_MIN_MS = 30 * 1000;
let lastSeenVersion;
let lastListInvalidateAt = 0;

export function useInventorySyncStatus(options = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: inventoryKeys.syncStatus(),
    queryFn: async () => (await getInventorySyncStatus()).data,
    staleTime: 5 * 1000,
    refetchInterval: SYNC_POLL_MS, // pauses while the tab is hidden (react-query default)
    refetchOnWindowFocus: true,
    ...options,
  });

  const version = query.data?.version;
  useEffect(() => {
    if (version == null) return undefined;
    if (lastSeenVersion === undefined) {
      // First sighting in this session: the list that is loading now already
      // reflects this version
      lastSeenVersion = version;
      return undefined;
    }
    if (version === lastSeenVersion) return undefined;
    const fire = () => {
      lastSeenVersion = version;
      lastListInvalidateAt = Date.now();
      queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
    };
    const wait = lastListInvalidateAt + LIST_REFETCH_MIN_MS - Date.now();
    if (wait <= 0) {
      fire();
      return undefined;
    }
    const timer = setTimeout(fire, wait);
    return () => clearTimeout(timer);
  }, [version, queryClient]);

  return query;
}
